import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import ImageScaleView from '@play-co/timestep-core/lib/ui/ImageScaleView';
import { isLiffFriendshipApiSupported } from 'src/lib/gcinstant';
import { requestAndVerifyChatbotSubscription } from './resubscribe.sequence';
import { AssetGroup } from '@play-co/timestep-core';
import sounds from 'src/lib/sounds';

import type { ResubscribeCloseReason } from 'src/replicant/chatbot/chatbot.analytics.schema';

type CloseHandler = (result: ResubscribeCloseReason) => void;

export default class PopupResubscribeForce extends PopupBasic<CloseHandler> {
  private close: CloseHandler;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      ...opts,
      zIndex: 10000,
      width: 558,
      height: 732,
      tracking: { feature: 'resubscribe', $subFeature: 'resubscribe-force' },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    this.message.updateOpts({
      y: this.box.style.height - 360,
      height: 160,
      wordWrap: false,
    });

    new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/resubscribe/app_image.png',
      x: this.box.style.width / 2 - 10,
      y: 240,
      width: 242,
      height: 293,
      centerOnOrigin: true,
    });

    const subButton = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      localeText: () => i18n('resubscribe.force.cta'),
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.handleResubscribe(subButton);
      },
      icon: {
        image: 'assets/ui/resubscribe/add_friend_icon.png',
        width: 57,
        height: 52,
        offset: {
          x: -160,
          y: 0,
        },
      },
      font: 'Body',
      fontSize: 42,
      width: 422,
      height: 82,
      x: this.box.style.width / 2,
      y: this.box.style.height - 160,
      labelOffsetY: -4,
      labelPaddingX: 0,
      labelPaddingY: 0,
      centerOnOrigin: true,
      tracking: {
        feature: 'resubscribe',
        $subFeature: 'resubscribe-force-friend',
      },
    });

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      localeText: () => i18n('resubscribe.force.cta2'),
      onClick: async () => {
        sounds.sfx.play('ok');
        this.close('play_on_app');
      },
      icon: {
        image: 'assets/ui/resubscribe/app_icon.png',
        width: 52,
        height: 52,
        offset: {
          x: -166,
          y: -4,
        },
      },
      font: 'Body',
      fontSize: 42,
      width: 422,
      height: 82,
      x: this.box.style.width / 2,
      y: this.box.style.height - 70,
      labelOffsetY: -4,
      labelOffsetX: 20,
      labelPaddingX: 60,
      labelPaddingY: 0,
      centerOnOrigin: true,
      tracking: {
        feature: 'resubscribe',
        $subFeature: 'resubscribe-force-native',
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/resubscribe/app_image.png',
      'assets/ui/resubscribe/add_friend_icon.png',
      'assets/ui/resubscribe/app_icon.png',
    ]);
  }

  /**
   * LIFF path: requests friendship, closes with 'subscribe' or re-enables button.
   * Legacy path: closes with 'subscribe' — the sequence handles subscribeBotAsync.
   */
  private async handleResubscribe(button: ButtonScaleViewWithText) {
    button.setDisabled(true);

    if (isLiffFriendshipApiSupported()) {
      const isSubscribedToChatbot = await requestAndVerifyChatbotSubscription();
      if (isSubscribedToChatbot) {
        this.close('subscribe');
      } else {
        button.setDisabled(false);
      }
    } else {
      this.close('subscribe');
    }
  }

  public init(_: {}) {
    super.init({
      title: i18n('resubscribe.force.title'),
      message: i18n('resubscribe.force.message', { value: 50 }),
    });
  }
}
